import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ViajeDTO } from '../../../dtos/viaje.dto';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { ConductorDTO } from '../../../dtos/conductor.dto';
import { ConductorService } from '../../services/conductor.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { ViajeService } from '../../services/viaje.service';
import { AutoRefreshService } from '../../services/auto-refresh.service';
import { MovilDTO } from '../../../dtos/movil.dto';
import { AppMessage } from '../../app.message';
import { ToastrService } from 'ngx-toastr';
import { EventService } from '../../services/event.service';
import { UserService } from '../../services/user.service';

// Constante para el estado de viaje en curso
const ESTADO_VIAJE_EN_CURSO = 'EN_CURSO';

// CON-1004: estados en los que no se permite editar el viaje (asignar/seleccionar)
const ESTADOS_VIAJE_NO_EDITABLE = ['EN_CURSO', 'NO_INICIADO', 'FINALIZADO'];

@Component({
  selector: 'app-conductor',
  standalone: true,
  imports: [CommonModule, FormsModule, InfiniteScrollDirective],
  templateUrl: './conductor.component.html',
  styleUrl: './conductor.component.scss',
  providers: [DatePipe]
})
export class ConductorComponent implements OnInit, OnDestroy /*OnChanges*/ {

  viajeSeleccionado: ViajeDTO;
  subscriberViajeSeleccionado: Subscription;
  private autoRefreshSubscription: Subscription;
  private viajeActualizadoMqttSubscription: Subscription;

  tipoBusqueda = 'COMPLETA';

  @Output() viewChange = new EventEmitter<{ type: string, viajeSeleccionado: ViajeDTO | null }>();

  asignandoConductor : boolean = false;

  // Getter para usar en el template
  get isViajeEnCurso(): boolean {
    return this.viajeSeleccionado?.estado === ESTADO_VIAJE_EN_CURSO;
  }

  // CON-1004: bloquea edición si el viaje está EN_CURSO, NO_INICIADO o FINALIZADO
  get isEdicionBloqueada(): boolean {
    return ESTADOS_VIAJE_NO_EDITABLE.includes(this.viajeSeleccionado?.estado);
  }

  conductores: ConductorDTO[] = [];
  filteredConductores: ConductorDTO[] = [];
  loading: boolean = false;
  error: string | null = null;
  selectedConductor: ConductorDTO | null = null;
  selectedIndex: number = -1;
  searchText: string = '';
  showReemplazo: boolean = true; // Default to true to match the initial HTML state
  responseMsg: string = '';

  // Sorting variables
  orderBy: string = 'visibleId';
  orderAsc: boolean = true;

  // Infinite scroll variables
  private pageSize: number = 50;
  public page: number = 0;
  private hasNext: boolean = true;
  public desactivarInfiniteScroll: boolean = false;
  private isLoadingSort: boolean = false;
  private searchSubject = new Subject<string>();

  constructor(
    private apiService: ApiService,
    private conductorService: ConductorService,
    private viajeService: ViajeService,
    private autoRefreshService: AutoRefreshService,
    private toastr: ToastrService,
    private eventService: EventService,
    private userService: UserService
  ) { }

  ngOnInit(): void {

    this.subscriberViajeSeleccionado = this.viajeService.viajeSeleccionadoDespachador$
      .pipe(distinctUntilChanged((a, b) => a?.uuid === b?.uuid))
      .subscribe(viajeSeleccionado => {
        this.viajeSeleccionado = viajeSeleccionado;
        if (this.viajeSeleccionado) {
          this.loadConductores(true);
        }
      });

    // Configure search debounce
    this.searchSubject.pipe(
      debounceTime(500), // Wait for 500ms after the last event
      distinctUntilChanged() // Only emit if value is different from previous
    ).subscribe(searchText => {
      this.searchText = searchText;
      this.loadConductores(true); // Reset and load with search text
    });

    // Suscribirse al evento de auto-refresh del contador
    this.autoRefreshSubscription = this.autoRefreshService.refresh$.subscribe(() => {
      console.log('Conductor component: recibiendo evento de auto-refresh');
      if (this.viajeSeleccionado) {
        this.loadConductores(true);
      }
    });

    // Suscribirse al evento MQTT de viaje actualizado
    this.viajeActualizadoMqttSubscription = this.eventService.subscribe('VIAJE_ACTUALIZADO_MQTT_EVENT', (viaje) => {
      if (this.viajeSeleccionado.uuid === viaje.uuid) {
        // Actualizar el viaje seleccionado con la nueva información
        this.viajeSeleccionado = { ...viaje };
        // Refresh the conductor list if needed
        this.loadConductores(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriberViajeSeleccionado.unsubscribe();
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
    if (this.viajeActualizadoMqttSubscription) {
      this.viajeActualizadoMqttSubscription.unsubscribe();
    }
  }

  /*
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sucursalUuid']?.previousValue && changes['sucursalUuid'].previousValue != changes['sucursalUuid'].currentValue) {
      this.loadConductores(true);
    }
  }
  */

  loadConductores(reset: boolean = false): void {
    this.loading = true;
    this.error = null;

    if (reset) {
      this.page = 0;
      this.hasNext = true;
      this.desactivarInfiniteScroll = false;
      if (!this.isLoadingSort) {
        this.conductores = [];
        this.filteredConductores = [];
      }
    }

    if (!this.viajeSeleccionado?.sucursalOrigen?.uuid) {
      this.toastr.warning("El viaje no tiene sucursal de origen", 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
      return;
    }

    const sucursalUuid = this.viajeSeleccionado.sucursalOrigen.uuid.trim();
    this.apiService.getListaConductores(
      this.page++,
      this.pageSize,
      sucursalUuid,
      this.searchText,
      this.orderBy,
      this.orderAsc,
      false,
      '',
      this.tipoBusqueda
    ).subscribe({
      next: (response) => {
        const newConductores = response.data.list;

        if (response.msg) {
          this.responseMsg = response.msg;
        }

        this.hasNext = response.data.hasNext;

        this.conductores = reset ? newConductores : [...this.conductores, ...newConductores];
        this.filteredConductores = this.conductores;
        this.loading = false;
        this.isLoadingSort = false;

        this.desactivarInfiniteScroll = !this.hasNext;
        if (reset && this.filteredConductores.length > 0) {
          this.selectConductor(this.filteredConductores[0], 0);
        } else if (this.filteredConductores.length === 0) {
          this.selectedConductor = null;
          this.selectedIndex = -1;
        }

        console.log('Updated conductores list', {
          totalCount: this.conductores.length,
          filteredCount: this.filteredConductores.length
        });
      },
      error: (err) => {
        console.error('Error loading conductores:', err);
        this.error = 'Error al cargar los conductores. Por favor, intente nuevamente.';
        this.loading = false;
        this.isLoadingSort = false;
        this.desactivarInfiniteScroll = true;
        this.toastr.error((err.error?.msg || '') + (err['requestId'] ? ' Code: ' + err['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    });
  }

  onScroll(): void {
    if (!this.loading && this.hasNext) {
      console.log('Loading more conductores...');
      this.loadConductores();
    } else {
      console.log('Scroll event ignored', {
        loading: this.loading,
        hasNext: this.hasNext,
        reason: this.loading ? 'Currently loading' : 'No more data'
      });
    }
  }

  onSearchChange(text: string): void {
    this.searchSubject.next(text);
  }

  onSort(field: string): void {
    if (this.orderBy === field) {
      this.orderAsc = !this.orderAsc;
    } else {
      this.orderBy = field;
      this.orderAsc = true;
    }
    // Set loading flag to keep current data visible while sorting
    this.isLoadingSort = true;
    this.loadConductores(true);
  }

  selectConductor(conductor: ConductorDTO, index: number): void {
    // Si el viaje no es editable (en curso, no iniciado o finalizado), no permitir la selección
    if (this.isEdicionBloqueada) {
      return;
    }

    // Si el conductor no está disponible, no permitir la selección
    if (conductor.estado !== 'DISPONIBLE') {
      return;
    }

    this.selectedConductor = conductor;
    this.selectedIndex = index;

    // Store the selected conductor in the service for potential use in other components
    this.conductorService.setConductorSeleccionado(conductor);
  }

  viewConductorDetails(conductor: ConductorDTO, event: Event): void {
    event.stopPropagation();

    // Siempre permitir ver detalles, incluso si el viaje está confirmado
    const index = this.conductores.findIndex(c => c === conductor);
    if (index !== -1) {
      // Si el viaje está en curso, solo actualizamos la UI sin cambiar la selección
      if (this.isEdicionBloqueada) {
        this.selectedConductor = conductor;
        this.selectedIndex = index;
      } else {
        this.selectConductor(conductor, index);
      }
    }

    console.log('View details for conductor:', conductor);
  }

  closeReemplazo(): void {
    this.showReemplazo = false;
    console.log('Close replacement section');
  }

  toggleReemplazo(): void {
    this.showReemplazo = !this.showReemplazo;
    console.log('Toggle replacement section:', this.showReemplazo);
  }

  tienePermisoAsignarConductor(): boolean {
    return this.userService.tienePermiso('VIAJE_CAMBIAR_CONDUCTOR');
  }

  asignarConductor(): void {
    // Si el viaje no es editable (en curso, no iniciado o finalizado), no permitir la asignación
    if (this.isEdicionBloqueada) {
      return;
    }

    if (!this.selectedConductor || !this.viajeSeleccionado) {
      return;
    }

    this.asignandoConductor = true;
    this.apiService.putCambiarConductor(this.viajeSeleccionado.uuid, this.selectedConductor.uuid)
    .pipe(
      finalize(() => {
        this.asignandoConductor = false;
        this.loadConductores();
      })
    )
    .subscribe({
      next: (response) => {
        if (response.success) {
          this.toastr.success(response.msg, '', { timeOut: environment.toastSuccessTimeout, extendedTimeOut: environment.toastSuccessTimeout, closeButton: true, tapToDismiss: false });
          this.viajeSeleccionado.conductor = this.selectedConductor;
          //CON-1057: cambiar el conductor anula la confirmacion en servicios, asi que el viaje
          //vuelve a pedir "Confirmar & Imprimir" y deja de ofrecer la reimpresion del vale
          this.viajeSeleccionado.confirmado = false;
        } else {
          this.toastr.warning(response.msg || 'Error al asignar el conductor', 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
        }
      },
      error: (error) => {
        console.log('error asignar conductor', error);
        const msg = error.error && error.error.msg ? error.error.msg : AppMessage.ERROR_PUT_ASIGNAR_CONDUCTOR_DESPACHADOR_WS;
        // Si el conductor ya fue asignado a otro viaje (conflicto), limpiar la selección de reemplazo
        if (error.status === 409) {
          this.selectedConductor = null;
          this.selectedIndex = -1;
          this.conductorService.setConductorSeleccionado(null);
        }
        this.toastr.error(msg + (error['requestId'] ? ' Code: ' + error['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    });
  }

  /**
   * Muestra el tiempo estimado de llegada si el conductor está en ruta
   */
  getTiempoLlegada(conductor: ConductorDTO): string {
    if (conductor.estadoFechaTerminoPlanificado !== null) {
      return formatDate(conductor.estadoFechaTerminoPlanificado, 'HH:mm', 'en-US');
    }
    return '--';
  }

  /**
   * Muestra las horas disponibles respecto a la jornada de trabajo y horas trabajadas
   */
  getHorasDisponibles(conductor: ConductorDTO): string {
    if (conductor.jornadaEnCurso && conductor.jornadaEnCurso.minutosRestantes) {
      // Convertir minutos a formato de horas y minutos
      return this.convertirMinutosAHHMM(conductor.jornadaEnCurso.minutosRestantes);
    }
    return '--';
  }

  calcularTiempoDisponible(conductor: ConductorDTO): string {

    if(!conductor || !conductor.jornadaEnCurso || !conductor.jornadaEnCurso.fechaTerminoPlanificado){
      return "--";
    }

    const fechaPlanificada = moment.utc(conductor.jornadaEnCurso.fechaTerminoPlanificado).local();
    if (!fechaPlanificada.isValid()) return '--';

    const ahora = moment();
    const diferenciaMin = fechaPlanificada.diff(ahora, 'minutes');
    const duracion = moment.duration(Math.abs(diferenciaMin), 'minutes');

    const hh = String(Math.floor(duracion.asHours())).padStart(2, '0');
    const mm = String(duracion.minutes()).padStart(2, '0');

    return diferenciaMin >= 0 ? `${hh}:${mm}` : `-${hh}:${mm}`;
  }

  /**
   * Muestra si tienen formado el anexo de horas extras y cuántas lleva en la jornada
   */
  getHorasExtras(conductor: ConductorDTO): string {
    // Verificar si tiene horas extras autorizadas
    const tieneHorasExtras = conductor.hhExtraAutorizadas;

    if (!conductor.jornadaEnCurso) {
      return 'No';
    }

    if (tieneHorasExtras && conductor.jornadaEnCurso.horasExtra > 0) {
      // Si tiene horas extras autorizadas y tiene horas extras
      return `Sí /  ${this.convertirMinutosAHHMM(conductor.jornadaEnCurso.horasExtra)}`;
    } else if (tieneHorasExtras && conductor.jornadaEnCurso.horasExtra <= 0) {
      return 'Sí / 0';
    } else if (!tieneHorasExtras && conductor.jornadaEnCurso.horasExtra > 0) {
      return `No / ${this.convertirMinutosAHHMM(conductor.jornadaEnCurso.horasExtra)}`;
    }

    return 'No';
  }

  convertirMinutosAHHMM(minutos: number): string {
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${horas.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  cercanoATerminarJornada(conductor: ConductorDTO) {

    if (
      !conductor ||
      !conductor.jornadaEnCurso ||
      !conductor.jornadaEnCurso.fechaTerminoPlanificado
    ) {
      return false;
    }

    const fechaTermino = moment.utc(conductor.jornadaEnCurso.fechaTerminoPlanificado).local();
    const ahora = moment.now();
    const diferenciaEnMinutos = fechaTermino.diff(ahora, 'minutes');
    return diferenciaEnMinutos <= 31;
  }

  estaHaciendoHorasExtras(conductor: ConductorDTO): boolean {
    return !!conductor.jornadaEnCurso && conductor.jornadaEnCurso.horasExtra > 0;
  }

  /**
   * Navega al componente de viaje cuando se hace clic en el encabezado
   */
  navigateToViaje(): void {
    if (this.viajeSeleccionado) {
      this.viewChange.emit({ type: 'viaje', viajeSeleccionado: this.viajeSeleccionado });
    }
  }
}
